import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import ReviewModal from "@/components/ReviewModal";

const Stars = ({ value }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={16} className="text-gold" fill={n <= value ? "currentColor" : "none"} />
    ))}
  </div>
);

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Reviews = () => {
  const { t } = useLang();
  const { user, setAuthOpen } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [zoom, setZoom] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleLeave = () => {
    if (user && typeof user === "object") setModalOpen(true);
    else {
      toast.message(t("reviews.form.loginRequired"));
      setAuthOpen(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.review_id !== id));
      toast.success("OK");
    } catch {
      toast.error("Error");
    }
  };

  const canDelete = (r) =>
    user && typeof user === "object" && (user.role === "admin" || user.user_id === r.user_id);

  return (
    <section id="reviews" className="relative py-16 md:py-32 px-6 md:px-12 bg-background grain overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <Eyebrow>{t("reviews.badge")}</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl md:text-5xl text-foreground leading-tight">
                {t("reviews.title")}
              </h2>
              <p className="mt-4 text-muted-foreground font-sans leading-relaxed">{t("reviews.subtitle")}</p>
            </div>
            <button
              data-testid="leave-review-btn"
              onClick={handleLeave}
              className="group inline-flex items-center justify-center gap-2 bg-gold text-black font-sans font-semibold text-sm uppercase tracking-[0.15em] px-7 py-3.5 rounded-sm hover:bg-gold-deep transition-colors shrink-0"
            >
              <Plus size={17} />
              {t("reviews.leave")}
            </button>
          </div>
        </Reveal>

        {reviews.length === 0 ? (
          <p className="mt-12 text-muted-foreground font-sans">{t("reviews.empty")}</p>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-list">
            {reviews.map((r, i) => (
              <Reveal key={r.review_id} delay={(i % 3) * 0.07}>
                <div className="group relative h-full bg-card border border-border hover:border-gold/40 rounded-sm p-6 transition-colors">
                  {canDelete(r) && (
                    <button
                      data-testid={`delete-review-${i}`}
                      onClick={() => handleDelete(r.review_id)}
                      className="absolute top-4 right-4 text-muted-foreground hover:text-red-400 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  <div className="flex items-center gap-3">
                    {r.author_picture ? (
                      <img src={r.author_picture} alt="" className="w-11 h-11 rounded-full object-cover" />
                    ) : (
                      <span className="w-11 h-11 rounded-full bg-gold/15 text-gold flex items-center justify-center font-serif text-lg">
                        {initials(r.author_name)}
                      </span>
                    )}
                    <div>
                      <div className="font-sans font-semibold text-foreground text-sm">{r.author_name}</div>
                      <Stars value={r.rating} />
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-muted-foreground font-sans leading-relaxed">{r.text}</p>
                  {r.images?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {r.images.map((src, idx) => (
                        <button
                          key={idx}
                          onClick={() => setZoom(src)}
                          className="w-16 h-16 rounded-sm overflow-hidden border border-border hover:border-gold/60 transition-colors"
                        >
                          <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <ReviewModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={() => load()} />

      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[160] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setZoom(null)}
          >
            <button className="absolute top-5 right-5 text-white/70 hover:text-gold" onClick={() => setZoom(null)}>
              <X size={28} />
            </button>
            <img src={zoom} alt="" className="max-h-[88vh] max-w-[92vw] object-contain" />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Reviews;
